import React, { useState, useRef } from "react";
import { ToastContainer, toast } from "react-toastify";
import { MdUpload, MdLogout, MdDownload, MdRefresh, MdCloudUpload } from "react-icons/md";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import pythonApiClient from "../../services/api/pythonBaseApi";
import { rrhhApiConfig } from "../../utils/rrhhProyectoHelpers";

const CargaMasivaRemuneraciones = ({ show, onClose, onSaved, empleadosSinAsignar = [], montoAsignacionDefault = 0, proyectoId }) => {
  const fileInputRef = useRef(null);

  const [file,                 setFile]                = useState(null);
  const [fileForRevalidation,  setFileForRevalidation] = useState(null);
  const [isLoading,            setIsLoading]           = useState(false);
  const [isValidating,         setIsValidating]        = useState(false);
  const [progress,             setProgress]            = useState(0);
  const [results,              setResults]             = useState(null);
  const [fileValidationErrors, setFileValidationErrors]= useState([]);
  const [isFileValid,          setIsFileValid]         = useState(false);

  const showToast = (type, message) =>
    toast[type]?.(message, { position: "bottom-right", autoClose: 3500 });

  // ── Template ────────────────────────────────────────────────────────────
  const downloadTemplate = () => {
    let data = [];
    const asigHeader = `VALOR ASIGNACIÓN (Actual: ${montoAsignacionDefault})`;
    
    if (empleadosSinAsignar.length > 0) {
      data = empleadosSinAsignar.map(emp => ({
        'NOMBRE':                   `${emp.nombre_1 || ''} ${emp.apellido_1 || ''}`.trim(),
        'DOCUMENTO':                emp.numero_documento_identidad || '',
        'SUELDO BASE':              '',
        'ASIGNACIÓN FAMILIAR (SÍ/NO)': '',
        [asigHeader]:               '',
        'BONIFICACIÓN FIJA':        '',
        'OBSERVACIÓN BONIFICACIÓN': '',
      }));
    } else {
      data = [{
        'NOMBRE':                   'EJEMPLO EMPLEADO',
        'DOCUMENTO':                '12345678',
        'SUELDO BASE':              '1500',
        'ASIGNACIÓN FAMILIAR (SÍ/NO)': 'SÍ',
        [asigHeader]:               `${montoAsignacionDefault}`,
        'BONIFICACIÓN FIJA':        '0',
        'OBSERVACIÓN BONIFICACIÓN': '',
      }];
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template Remuneraciones");
    XLSX.writeFile(wb, "template_remuneraciones.xlsx");
    showToast("success", "Template de Remuneraciones descargado");
  };

  // ── Manejo de archivo ────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(ext)) {
      showToast("error", "Por favor seleccione un archivo Excel válido (.xlsx o .xls)");
      e.target.value = '';
      return;
    }

    setFileForRevalidation(selectedFile);
    setIsValidating(false);
    setFileValidationErrors([]);
    setIsFileValid(true); 
    setResults(null);
    setFile(selectedFile);
  };

  // ── Envío ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { showToast("error", "Por favor seleccione un archivo Excel"); return; }

    setIsLoading(true);
    setProgress(0);
    setResults(null);
    setFileValidationErrors([]);

    try {
      const formData = new FormData();
      formData.append("file", file);

      setProgress(25);
      const res = await pythonApiClient.post(
        "/rrhh/carga-masiva-remuneraciones/validate",
        formData,
        rrhhApiConfig(proyectoId, { "Content-Type": "multipart/form-data" })
      );
      setProgress(75);

      const r = res.data;
      if (r?.success === false) {
        setFileValidationErrors(r.errores || []);
        setIsFileValid(false);
        showToast("error", "Errores en la carga. Revise los detalles.");
      } else {
        setResults({
          nuevos: r.nuevos || 0,
          message: r.message
        });
        showToast("success", "Carga masiva completada exitosamente");
        if (onSaved) onSaved();
      }
      setProgress(100);
    } catch (err) {
      const data = err.response?.data;
      if (data?.errores) {
        setFileValidationErrors(data.errores);
        setIsFileValid(false);
        showToast("error", data.message || "Errores de validación");
      } else {
        showToast("error", data?.message || data?.detail || "Error interno procesando el archivo, revise la información llenada.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFile(null); setFileForRevalidation(null);
    setResults(null); setProgress(0);
    setFileValidationErrors([]); setIsFileValid(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    showToast("info", "Formulario reiniciado");
  };

  // ── Error table helper ────────────────────────────────────────────────────
  const ErrorTable = ({ errores }) => (
    <div className="table-responsive mt-3">
      <table className="table mb-0" style={{ fontSize: '0.9rem' }}>
        <thead className="table-dark">
          <tr>
            <th style={{ width: 110, backgroundColor: '#343a40', color: '#fff' }}>📍 Fila Excel</th>
            <th style={{ backgroundColor: '#343a40', color: '#fff' }}>🚫 Errores de Validación</th>
          </tr>
        </thead>
        <tbody>
          {errores.map((err, idx) => (
            <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#fff' }}>
              <td className="fw-bold text-center">
                <span className="badge bg-danger fs-6">{err.fila}</span>
              </td>
              <td>
                <ul className="list-unstyled mb-0">
                  {(Array.isArray(err.errores) ? err.errores : [err.errores]).map((e, i) => (
                    <li key={i} className="mb-1" style={{ color: '#dc3545' }}>
                      <i className="fas fa-times-circle text-danger me-2" />
                      {e}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ── JSX ───────────────────────────────────────────────────────────────────
  if (!show) return null;

  return (
    <>
      {/* Estilos locales */}
      <style>{`
        .dh-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
          background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 1050;
        }
        .dh-modal-content {
          background: #fff; width: 95%; max-width: 1000px;
          height: 90vh; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.2);
          display: flex; flex-direction: column; overflow: hidden;
        }
        .dh-modal-header {
          background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
          color: #fff; padding: 1.2rem 1.5rem; display: flex;
          justify-content: space-between; align-items: center;
        }
        .dh-modal-title { font-size: 1.25rem; font-weight: 600; margin: 0; display: flex; align-items: center; gap: 0.5rem; }
        .dh-modal-body { padding: 1.5rem; flex: 1; overflow-y: auto; background: #f8f9fa; }
        .dh-modal-footer {
          padding: 1rem 1.5rem; background: #fff; border-top: 1px solid #e9ecef;
          display: flex; justify-content: flex-end; gap: 0.75rem;
        }
        .dh-btn {
          padding: 0.5rem 1rem; border-radius: 6px; font-weight: 500;
          display: inline-flex; align-items: center; gap: 0.5rem; border: none; cursor: pointer; transition: all 0.2s;
        }
        .dh-btn-outline { background: transparent; border: 1px solid #ced4da; color: #495057; }
        .dh-btn-outline:hover { background: #e9ecef; }
        .dh-btn-primary { background: #0d6efd; color: #fff; }
        .dh-btn-primary:hover { background: #0b5ed7; }
        .dh-btn-success { background: #198754; color: #fff; }
        .dh-btn-success:hover { background: #157347; }
        .dh-btn-danger { background: #dc3545; color: #fff; }
        .dh-btn-danger:hover { background: #bb2d3b; }
      `}</style>

      <div className="dh-modal-overlay">
        <div className="dh-modal-content">
          {/* Header */}
          <div className="dh-modal-header">
            <h2 className="dh-modal-title">
              <MdCloudUpload size={24} />
              Completar Masivo de Remuneraciones
            </h2>
          </div>

          {/* Body */}
          <div className="dh-modal-body">
            <div className="row g-4 h-100">
              
              {/* Columna Izquierda: Instrucciones */}
              <div className="col-lg-5 d-flex flex-column gap-3 h-100">
                <div className="card shadow-sm border-0 flex-grow-1" style={{ borderRadius: 10 }}>
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title text-primary mb-3">
                      <MdUpload className="me-2" /> Instrucciones
                    </h5>
                    <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: '100%' }}>
                      <p className="text-muted small mb-2">Siga estos pasos para completar masivamente las remuneraciones:</p>
                      <ol className="small ps-3 mb-3 text-secondary">
                        <li className="mb-1">Descargue el archivo de plantilla Excel usando el botón inferior.</li>
                        <li className="mb-1">El archivo ya vendrá pre-llenado con los nombres y DNI de los empleados faltantes.</li>
                        <li className="mb-1">Complete las columnas faltantes (Sueldo, Asignación Familiar, etc.).</li>
                        <li className="mb-1">Guarde el archivo y súbalo en la sección derecha.</li>
                      </ol>

                      <div className="p-2 bg-light rounded border border-info mb-3">
                        <p className="mb-1 fw-bold text-info" style={{ fontSize: '0.85rem' }}>Columnas requeridas:</p>
                        <ul style={{ paddingLeft: '1.2rem', marginBottom: 0, fontSize: '0.8rem' }}>
                          <li><strong>NOMBRE</strong> (Pre-llenado)</li>
                          <li><strong>DOCUMENTO</strong> (Pre-llenado)</li>
                          <li><strong>SUELDO BASE</strong> (Numérico &gt;= 0)</li>
                          <li><strong>ASIGNACIÓN FAMILIAR (SÍ/NO)</strong></li>
                          <li><strong>VALOR ASIGNACIÓN (Actual: {montoAsignacionDefault})</strong> (Opcional, por defecto 0)</li>
                          <li><strong>BONIFICACIÓN FIJA</strong> (Numérico &gt;= 0)</li>
                          <li><strong>OBSERVACIÓN BONIFICACIÓN</strong> (Texto opcional)</li>
                        </ul>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-top">
                      <button onClick={downloadTemplate} className="dh-btn dh-btn-success w-100 justify-content-center">
                        <MdDownload size={18} /> Descargar Excel a llenar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Columna Derecha: Carga y Resultados */}
              <div className="col-lg-7 d-flex flex-column h-100">
                <div className="card shadow-sm border-0 flex-grow-1" style={{ borderRadius: 10 }}>
                  <div className="card-body d-flex flex-column">
                    <h5 className="card-title text-primary mb-3">
                      <MdUpload className="me-2" /> Subir Archivo
                    </h5>

                    {/* Zona de input file */}
                    <div className="mb-4">
                      <label className="form-label fw-bold text-secondary small">Seleccione su archivo completado:</label>
                      <input
                        type="file"
                        className="form-control form-control-lg"
                        accept=".xlsx, .xls"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        disabled={isLoading || isValidating}
                        style={{ fontSize: '0.9rem' }}
                      />
                    </div>

                    {/* Progreso */}
                    {(isLoading || isValidating) && (
                      <div className="mb-4">
                        <div className="d-flex justify-content-between mb-1 small text-muted">
                          <span>{isValidating ? "Validando archivo..." : "Procesando carga..."}</span>
                          <span>{progress}%</span>
                        </div>
                        <div className="progress" style={{ height: 10 }}>
                          <div
                            className={`progress-bar progress-bar-striped progress-bar-animated ${isValidating ? "bg-info" : "bg-primary"}`}
                            role="progressbar"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Resultados de Error */}
                    {fileValidationErrors.length > 0 && !isFileValid && (
                      <div className="flex-grow-1 overflow-auto mt-2" style={{ border: '1px solid #f5c2c7', borderRadius: 8 }}>
                        <div className="bg-danger text-white px-3 py-2 fw-bold d-flex align-items-center" style={{ fontSize: '0.9rem' }}>
                          <i className="fas fa-exclamation-triangle me-2" /> Se encontraron errores en el archivo
                        </div>
                        <ErrorTable errores={fileValidationErrors} />
                      </div>
                    )}

                    {/* Resultados de Éxito */}
                    {results && !fileValidationErrors.length && (
                      <div className="mt-3">
                        <div className="alert alert-success d-flex align-items-center mb-0" role="alert">
                          <i className="fas fa-check-circle fs-4 me-3" />
                          <div>
                            <h6 className="alert-heading mb-1 fw-bold">¡Carga exitosa!</h6>
                            <p className="mb-0 small">{results.message}</p>
                          </div>
                        </div>
                        <div className="row text-center mt-3 g-2">
                          <div className="col-12">
                            <div className="p-3 bg-success bg-opacity-10 rounded border border-success border-opacity-25 h-100">
                              <div className="fs-3 fw-bold text-success">{results.nuevos}</div>
                              <div className="small text-muted fw-bold">Remuneraciones Asignadas</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Estado Listo para cargar */}
                    {isFileValid && !results && !fileValidationErrors.length && !isLoading && (
                      <div className="mt-4 p-3 bg-primary bg-opacity-10 border border-primary border-opacity-25 rounded text-center">
                        <i className="fas fa-file-excel fs-1 text-primary mb-2" />
                        <h6 className="fw-bold text-primary mb-1">Archivo listo para procesar</h6>
                        <p className="small text-muted mb-0">Haga clic en "Cargar Datos" para iniciar.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Footer */}
          <div className="dh-modal-footer">
            <button type="button" className="dh-btn dh-btn-outline" onClick={onClose} disabled={isLoading || isValidating}>
              <MdLogout size={18} /> Cerrar
            </button>
            <button type="button" className="dh-btn dh-btn-danger" onClick={resetForm} disabled={(!file && !results && !fileValidationErrors.length) || isLoading || isValidating}>
              <MdRefresh size={18} /> Limpiar
            </button>
            <button type="button" className="dh-btn dh-btn-primary" onClick={handleSubmit} disabled={!isFileValid || isLoading || isValidating || results}>
              <MdCloudUpload size={18} /> Cargar Datos
            </button>
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default CargaMasivaRemuneraciones;
